import React, { useEffect } from 'react'
import { Button, Loader, Modal, ProgressBar } from 'is24-corecss'
import { IconS24CheckmarkCircle48Filled } from '@is24/cosma-ui-icons'
import { useTranslation } from 'react-i18next'
import styles from './AiDataExtractionAnalyzingModal.module.css'

export enum ProcessingState {
  Preparing = 'preparing',
  Analyzing = 'analyzing',
  Transferring = 'transferring',
  Completing = 'completing',
  Done = 'done',
  Error = 'error',
}

const stateToPercent: Record<ProcessingState, number> = {
  [ProcessingState.Preparing]: 10,
  [ProcessingState.Analyzing]: 30,
  [ProcessingState.Transferring]: 50,
  [ProcessingState.Completing]: 100,
  [ProcessingState.Done]: 100,
  [ProcessingState.Error]: 50,
}

const stateToPercentLabel: Record<ProcessingState, string> = {
  [ProcessingState.Preparing]: '10 %',
  [ProcessingState.Analyzing]: '30 %',
  [ProcessingState.Transferring]: '50 %',
  [ProcessingState.Completing]: '100 %',
  [ProcessingState.Done]: '100 %',
  [ProcessingState.Error]: '50 %',
}

const stateToLabelKey: Record<ProcessingState, string> = {
  [ProcessingState.Preparing]: 'wizard.uploadAiBanner.analyzingModal.tasks.preparing',
  [ProcessingState.Analyzing]: 'wizard.uploadAiBanner.analyzingModal.tasks.analyzing',
  [ProcessingState.Transferring]: 'wizard.uploadAiBanner.analyzingModal.tasks.transferring',
  [ProcessingState.Completing]: 'wizard.uploadAiBanner.analyzingModal.tasks.completing',
  [ProcessingState.Done]: 'wizard.uploadAiBanner.analyzingModal.tasks.done',
  [ProcessingState.Error]: 'wizard.uploadAiBanner.analyzingModal.tasks.error',
}

export interface AiDataExtractionAnalyzingModalProps {
  visible: boolean
  state: ProcessingState
  onRetry?: () => void
}

export const AiDataExtractionAnalyzingModal = ({ visible, state, onRetry }: AiDataExtractionAnalyzingModalProps) => {
  const { t } = useTranslation()

  const title = t('wizard.uploadAiBanner.analyzingModal.title')
  const subtitle = t('wizard.uploadAiBanner.analyzingModal.subtitle')
  const taskLabel = t(stateToLabelKey[state])
  const percentLabel = stateToPercentLabel[state]
  const progress = stateToPercent[state]
  const isError = state === ProcessingState.Error
  const isDone = state === ProcessingState.Done

  useEffect(() => {
    if (!visible) {
      return
    }

    const preventBackdropClose = (event: MouseEvent) => {
      if (!(event.target instanceof HTMLElement) || event.target.dataset.testid !== 'modal-backdrop') {
        return
      }

      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()
    }

    const preventEscapeClose = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' && event.key !== 'Esc') {
        return
      }

      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()
    }

    window.addEventListener('mousedown', preventBackdropClose, true)
    window.addEventListener('keydown', preventEscapeClose, true)

    return () => {
      window.removeEventListener('mousedown', preventBackdropClose, true)
      window.removeEventListener('keydown', preventEscapeClose, true)
    }
  }, [visible])

  return (
    <Modal.Root isOpen={visible}>
      <Modal.Backdrop>
        <Modal.Surface width={500}>
          <Modal.Body>
            <div className={styles.modalContent} data-testid='ai-analyzing-modal'>
              <section className={styles.introSection}>
                {isDone ? (
                  <span className={styles.successIcon} data-testid='ai-analyzing-success-icon'>
                    <IconS24CheckmarkCircle48Filled />
                  </span>
                ) : (
                  <Loader variant='secondary' size='medium' colorSet='darkGray' />
                )}
                <section className={styles.copyBlock}>
                  <h2 className='font-heading-xsmall-bold'>{title}</h2>
                  <p className='font-body-medium-regular'>{subtitle}</p>
                </section>
              </section>
              <section className={styles.progressSection}>
                <div className={styles.progressRow}>
                  <span className={`font-body-medium-regular ${styles.progressRowLabel}`}>{taskLabel}</span>
                  <span className='font-body-medium-regular'>{percentLabel}</span>
                </div>
                <div className={styles.progressBarWrapper}>
                  <ProgressBar.Root type='horizontal' size='large' progress={progress} aria-label={taskLabel}>
                    <ProgressBar.Track>
                      <ProgressBar.Indicator />
                    </ProgressBar.Track>
                  </ProgressBar.Root>
                </div>
              </section>
              {isError && onRetry && (
                <section className={styles.errorSection}>
                  <p className='font-body-medium-regular'>{t('wizard.uploadAiBanner.analyzingModal.errorText')}</p>
                  <Button variant='filledDefault' onClick={onRetry} data-testid='ai-analyzing-retry'>
                    {t('wizard.uploadAiBanner.analyzingModal.retry')}
                  </Button>
                </section>
              )}
            </div>
          </Modal.Body>
        </Modal.Surface>
      </Modal.Backdrop>
    </Modal.Root>
  )
}
